import { useEffect, useRef, useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { Measurement } from '../../../domain/Measurement';
import type { AlertEvent } from '../../../domain/AlertEvent';
import { componentDisplayName, enabledOfKind } from '../../../domain/Component';
import type { IrrigationEvent } from '../../../domain/Irrigation';
import type { IrrigationDailySummary } from '../../ports/IrrigationRepository';

export interface RiegoHistoryEvent extends IrrigationEvent {
  deviceName: string;
  valveName: string;
}

const valveNameFor = (device: Device, channel: number) => {
  const valve = device.components.find((c) => c.kind === 'valve' && c.channel === channel);
  return valve ? componentDisplayName(valve) : `Sector ${channel}`;
};

// Historial: lecturas, riegos (de todas las válvulas) y episodios de alerta,
// de todas las estaciones o de una.
export function useHistoryPage() {
  const {
    devices: deviceRepo,
    measurements: measurementRepo,
    alertEvents: alertEventRepo,
    irrigation: irrigationRepo,
  } = useRepositories();

  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [deviceEvents, setDeviceEvents] = useState<AlertEvent[]>([]);
  const [riegoEvents, setRiegoEvents] = useState<RiegoHistoryEvent[]>([]);
  const [dailyLiters, setDailyLiters] = useState<IrrigationDailySummary[]>([]);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const loadDevices = () => {
    deviceRepo
      .list()
      .then(({ devices: list }) => setDevices(list))
      .catch(() => {
        /* el error de mediciones ya se muestra abajo */
      });
  };

  const loadMeasurements = (filter: string) => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    const request = filter === 'all' ? measurementRepo.listRecent(100) : measurementRepo.listForDevice(filter, 100);
    request
      .then(({ measurements: list }) => setMeasurements(list))
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudieron cargar las mediciones.'))
      .finally(() => {
        loadedRef.current = true;
        setLoading(false);
      });
  };

  useIonViewWillEnter(() => {
    loadDevices();
    loadMeasurements(deviceFilter);
  });

  useEffect(() => {
    loadMeasurements(deviceFilter);
    if (deviceFilter === 'all') {
      setDeviceEvents([]);
      return;
    }
    alertEventRepo
      .listForDevice(deviceFilter, 100)
      .then(({ events }) => setDeviceEvents(events))
      .catch(() => setDeviceEvents([]));
  }, [deviceFilter]);

  useEffect(() => {
    const withValves = devices.filter(
      (d) => (deviceFilter === 'all' || d.id === deviceFilter) && enabledOfKind(d.components, 'valve').length > 0
    );
    if (withValves.length === 0) {
      setRiegoEvents([]);
      setDailyLiters([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      withValves.map((device) =>
        irrigationRepo
          .getStationHistory(device.id)
          .then((history) => ({ device, history }))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      const ok = results.filter((r): r is NonNullable<typeof r> => r !== null);
      const combined = ok.flatMap(({ device, history }) =>
        history.events.map((event) => ({
          ...event,
          deviceName: device.name,
          valveName: valveNameFor(device, event.valveChannel),
        }))
      );
      combined.sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''));
      setRiegoEvents(combined);
      setDailyLiters(deviceFilter === 'all' ? [] : (ok[0]?.history.dailyLiters ?? []));
    });
    return () => {
      cancelled = true;
    };
  }, [deviceFilter, devices, irrigationRepo]);

  const sortedDesc = [...measurements].sort((a, b) => (a.recordedAt < b.recordedAt ? 1 : -1));
  const selectedDevice = devices.find((d) => d.id === deviceFilter);

  return {
    devices,
    deviceFilter,
    setDeviceFilter,
    loading,
    error,
    sortedDesc,
    selectedDevice,
    deviceEvents,
    riegoEvents,
    dailyLiters,
  };
}
